import { memo } from 'react';
import { CustomerEquipmentTypeResponse } from '@/types';
import { ProductCard } from './ProductCard';
import { Package } from 'lucide-react';

interface ProductGridProps {
    products: CustomerEquipmentTypeResponse[];
    isLoading: boolean;
    emptyStateHeading: string;
    emptyStateBody: string;
}

export const ProductGrid = memo(({ products, isLoading, emptyStateHeading, emptyStateBody }: ProductGridProps) => {
    if (isLoading) {
        return (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {[...Array(8)].map((_, i) => (
                    <div
                        key={i}
                        className="overflow-hidden rounded-[1.75rem] border border-border/70 bg-[linear-gradient(180deg,rgba(255,255,255,1)_0%,rgba(249,245,239,1)_100%)] shadow-card"
                    >
                        <div className="delight-skeleton aspect-[4/5]" />
                        <div className="space-y-3 p-5">
                            <div className="delight-skeleton h-3 w-1/3 rounded-full" />
                            <div className="delight-skeleton h-5 w-4/5 rounded-full" />
                            <div className="delight-skeleton h-4 w-3/5 rounded-full" />
                            <div className="border-t border-border/70 pt-3">
                                <div className="delight-skeleton h-3 w-1/4 rounded-full" />
                                <div className="delight-skeleton mt-2 h-7 w-2/5 rounded-full" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // NOTE: `products` is expected to be already filtered by the caller —
    // ProductGrid is pure presentation so the page header can show an
    // accurate count of what's actually rendered. (Was filtering by
    // isVisibleToCustomers here; that's now done in Catalog.tsx alongside
    // the location / rentalMode / availability filters.)
    if (products.length === 0) {
        return (
            <div className="delight-sheen rounded-[1.75rem] border border-dashed border-border bg-white/80 p-12 text-left shadow-card">
                <Package className="mb-4 h-10 w-10 text-primary/40" aria-hidden="true" />
                <h3 className="font-display text-2xl text-foreground">{emptyStateHeading}</h3>
                <p className="mt-2 max-w-md text-sm leading-7 text-muted-foreground">
                    {emptyStateBody}
                </p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {products.map((product) => (
                <ProductCard key={product.id} product={product} availabilityIsDateScoped />
            ))}
        </div>
    );
});

ProductGrid.displayName = 'ProductGrid';
